import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { buildTranslationDraft } from './data/translationDraft';
import './TranslationDraftWorkspace.css';

const CATEGORIES = [
  { key: 'bullets', label: 'Experience', title: 'Choose the work that sounds like you.', hint: 'Start with a few useful statements. Keep what fits; edit the wording to make it yours.', preview: 'Experience statements' },
  { key: 'skills', label: 'Skills', title: 'Choose the skills you want to highlight.', hint: 'These are optional. Select the skills that describe work you actually performed.', preview: 'Selected skills' },
  { key: 'titles', label: 'Career ideas', title: 'Explore possible civilian directions.', hint: 'These are roles to research, not jobs you have held or qualifications you have earned.', preview: 'Career ideas to explore' },
  { key: 'keywords', label: 'Keywords', title: 'Add useful search terms.', hint: 'Choose a few relevant terms for future job searches. You do not need every suggestion.', preview: 'Search keywords' },
];
const PAGE_SIZE = 4;
const SESSION_COPY = 'This draft lasts while this page stays open. Approval does not save, publish, share, or add anything to a résumé.';

function Editor({ item, onSave, onCancel }) {
  const [value, setValue] = useState(item.text);
  const [error, setError] = useState('');
  const id = useId();
  return <div className="td-editor">
    <label htmlFor={id}>Your wording</label>
    <textarea id={id} autoFocus rows={item.type === 'bullets' ? 4 : 2} maxLength={1500} value={value} onChange={event => { setValue(event.target.value); setError(''); }} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} />
    {error && <p id={`${id}-error`} role="alert">{error}</p>}
    <div className="td-editor-actions">
      <button type="button" className="td-primary" onClick={() => value.trim() ? onSave(value.trim()) : setError('Enter some wording, or cancel to keep the original.')}>Apply edit</button>
      <button type="button" onClick={onCancel}>Cancel edit</button>
    </div>
  </div>;
}

export default function TranslationDraftWorkspace({ bio = '', occupation }) {
  const branch = occupation?.branch || '';
  const code = occupation?.code || '';
  const title = occupation?.title || '';
  // Primitive dependencies preserve a draft through ordinary Dashboard rerenders.
  const generated = useMemo(() => buildTranslationDraft({ bio, occupation: { branch, code, title } }), [bio, branch, code, title]);
  const [items, setItems] = useState(generated);
  const [selected, setSelected] = useState([]);
  const [category, setCategory] = useState('bullets');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [stage, setStage] = useState('choose');
  const [editingId, setEditingId] = useState(null);
  const [attested, setAttested] = useState(false);
  const [undoId, setUndoId] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [copyFallback, setCopyFallback] = useState(false);
  const [showApproved, setShowApproved] = useState(false);
  const [focusPending, setFocusPending] = useState(false);
  const [focusLibrary, setFocusLibrary] = useState(false);
  const headingRef = useRef(null);
  const libraryHeadingRef = useRef(null);
  const editorTriggerRef = useRef(null);
  const editButtonsRef = useRef({});
  const timersRef = useRef([]);
  const copyVersionRef = useRef(0);
  const instanceId = useId();

  useEffect(() => {
    timersRef.current.forEach(timer => window.clearTimeout(timer));
    timersRef.current = [];
    copyVersionRef.current += 1;
    setItems(generated);
    setSelected([]);
    setCategory('bullets');
    setQuery('');
    setPage(0);
    setStage('choose');
    setEditingId(null);
    setAttested(false);
    setUndoId(null);
    setFeedback('');
    setShowApproved(false);
    setCopyFallback(false);
  }, [generated]);
  useEffect(() => () => {
    timersRef.current.forEach(timer => window.clearTimeout(timer));
    copyVersionRef.current += 1;
  }, []);
  useEffect(() => {
    if (!focusPending) return;
    headingRef.current?.focus({ preventScroll: true });
    const rect = headingRef.current?.getBoundingClientRect();
    if (rect && (rect.top < 72 || rect.bottom > window.innerHeight)) headingRef.current.scrollIntoView({ behavior: 'auto', block: 'nearest' });
    setFocusPending(false);
  }, [focusPending, stage]);
  useEffect(() => {
    if (editingId || !editorTriggerRef.current) return;
    editButtonsRef.current[editorTriggerRef.current]?.focus({ preventScroll: true });
    editorTriggerRef.current = null;
  }, [editingId]);
  useEffect(() => {
    if (!focusLibrary) return;
    libraryHeadingRef.current?.focus({ preventScroll: true });
    libraryHeadingRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
    setFocusLibrary(false);
  }, [focusLibrary]);

  const chosen = items.filter(item => selected.includes(item.id));
  const meta = CATEGORIES.find(item => item.key === category);
  const filtered = items.filter(item => item.type === category && `${item.text} ${item.heading} ${item.source}`.toLowerCase().includes(query.toLowerCase().trim()));
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visibleItems = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const busy = stage === 'confirming';

  const changed = message => {
    setAttested(false);
    setCopyFallback(false);
    setShowApproved(false);
    copyVersionRef.current += 1;
    setFeedback(message);
  };
  const toggleSelection = item => {
    const removing = selected.includes(item.id);
    setSelected(current => removing ? current.filter(id => id !== item.id) : [...current, item.id]);
    setUndoId(removing ? item.id : null);
    changed(removing ? 'Removed from your draft. The suggestion is still available.' : 'Added to your draft. You can edit it before approval.');
  };
  const startEdit = item => {
    editorTriggerRef.current = item.id;
    setEditingId(item.id);
  };
  const closeEditor = () => {
    setEditingId(null);
  };
  const saveEdit = (item, text) => {
    setItems(current => current.map(candidate => candidate.id === item.id ? { ...candidate, text } : candidate));
    changed('Wording updated. Review your latest changes before approving.');
    closeEditor();
  };
  const goTo = next => {
    setStage(next);
    setFocusPending(true);
    setCopyFallback(false);
  };
  const approve = () => {
    if (!chosen.length || !attested || editingId || busy) return;
    setFeedback('');
    setStage('confirming');
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    timersRef.current.push(window.setTimeout(() => {
      setStage('approved');
      setFocusPending(true);
      setFeedback('Your selected translation is approved for this visit. It remains private.');
    }, reducedMotion ? 0 : 1100));
  };
  const approvedText = () => CATEGORIES.map(({ key, preview }) => {
    const group = chosen.filter(item => item.type === key);
    return group.length ? `${preview}\n${group.map(item => `• ${item.text}`).join('\n')}` : '';
  }).filter(Boolean).join('\n\n');
  const copyApproved = async () => {
    const version = copyVersionRef.current;
    try {
      await navigator.clipboard.writeText(approvedText());
      if (version === copyVersionRef.current) setFeedback('Approved translation copied. Paste it into a document you control.');
    } catch {
      if (version === copyVersionRef.current) {
        setCopyFallback(true);
        setFeedback('Copy is unavailable in this browser. Select and copy the text below.');
      }
    }
  };

  const renderEditor = item => <Editor key={item.id} item={item} onSave={text => saveEdit(item, text)} onCancel={closeEditor} />;
  const renderDraft = editable => <div className="td-document" aria-label="Selected translation preview">
    <div className="td-document-masthead"><span>MY CIVILIAN TRANSLATION</span><span>PRIVATE</span></div>
    <h4>Experience you can put into words.</h4>
    <p className="td-document-intro">Your selections, in your words. Career ideas are directions to explore.</p>
    {!chosen.length && <p className="td-empty">Your draft is empty. Choose at least one suggestion to continue.</p>}
    {CATEGORIES.map(({ key, preview }) => {
      const group = chosen.filter(item => item.type === key);
      if (!group.length) return null;
      return <section key={key} className="td-document-section"><h5>{preview}</h5>
        <ul>{group.map(item => <li key={item.id}>
          <div className="td-document-line"><span>{item.text}</span>
            {editable && <div className="td-document-tools">
              <button ref={node => { editButtonsRef.current[item.id] = node; }} type="button" disabled={Boolean(editingId)} onClick={() => startEdit(item)} aria-label={`Edit selected ${item.text}`}>Edit</button>
              <button type="button" disabled={Boolean(editingId)} onClick={() => toggleSelection(item)} aria-label={`Remove selected ${item.text}`}>Remove</button>
            </div>}
          </div>
          {editingId === item.id && renderEditor(item)}
        </li>)}</ul>
      </section>;
    })}
  </div>;

  return <section className="td-workspace" aria-label="Civilian translation builder" aria-busy={busy}>
    <div className="td-topline"><span className="td-eyebrow">CIVILIAN TRANSLATION{code ? ` / ${code}` : ''}</span><span className="td-private">Private · This visit only</span></div>
    <div className="td-heading-row"><div>
      <h3 ref={headingRef} tabIndex={-1}>{stage === 'choose' ? 'Build a translation that sounds like you.' : stage === 'review' ? 'Make sure this tells your story.' : busy ? 'Confirming your review…' : 'Your translation, approved.'}</h3>
      <p>{stage === 'choose' ? 'Choose what fits. Refine the words. Approve your selected draft.' : stage === 'review' ? 'Only the items you chose appear here. Edit or remove anything before approval.' : busy ? 'Your selected wording stays private.' : 'Your selected wording is ready to copy and use.'}</p>
    </div></div>
    <ol className="td-steps" aria-label="Translation progress">
      <li aria-current={stage === 'choose' ? 'step' : undefined}><span>1</span> Choose & edit</li>
      <li aria-current={stage === 'review' ? 'step' : undefined}><span>2</span> Review draft</li>
      <li aria-current={stage === 'approved' || busy ? 'step' : undefined}><span>3</span> Approve</li>
    </ol>
    <div className={`td-feedback${!feedback && !undoId ? ' is-empty' : ''}`} role="status" aria-live="polite" aria-atomic="true">
      <span>{feedback}</span>
      {undoId && stage !== 'approved' && !busy && <button type="button" onClick={() => {
        setSelected(current => current.includes(undoId) ? current : [...current, undoId]);
        setUndoId(null);
        changed('Restored to your draft. Review before approving.');
      }}>Undo removal</button>}
    </div>

    {!bio?.trim() ? <div className="td-empty"><h4>Start with your service story.</h4><p>Add a personal summary to your profile describing your responsibilities, equipment, and the people you supported.</p></div>
      : !items.length ? <div className="td-empty"><h4>We need a little more detail.</h4><p>Your summary did not support a specific translation yet. Describe a task you performed, what you worked with, and who you supported. Suggestions are based on your self-reported details, not verified facts.</p></div>
      : stage === 'choose' ? <>
        <div className="td-layout">
          <div className="td-library">
            <div className="td-category-nav" role="group" aria-label="Suggestion categories">{CATEGORIES.map(({ key, label }) => <button key={key} type="button" aria-pressed={category === key} disabled={Boolean(editingId)} onClick={() => { setCategory(key); setPage(0); setQuery(''); }}>
              {label}<span>{items.filter(item => item.type === key).length}</span>
            </button>)}</div>
            <div className="td-library-heading"><h4 ref={libraryHeadingRef} tabIndex={-1}>{meta.title}</h4><p>{meta.hint}</p></div>
            <label className="td-search" htmlFor={`${instanceId}-search`}><span>Find a suggestion</span><input id={`${instanceId}-search`} type="search" value={query} disabled={Boolean(editingId)} onChange={event => { setQuery(event.target.value); setPage(0); }} placeholder="Search these suggestions…" /></label>
            <div className="td-pager" aria-label="Suggestion pages">
              <span aria-live="polite">{filtered.length ? `${currentPage * PAGE_SIZE + 1}–${Math.min((currentPage + 1) * PAGE_SIZE, filtered.length)} of ${filtered.length}` : 'No matching suggestions'}</span>
              <div><button type="button" disabled={currentPage === 0 || Boolean(editingId)} onClick={() => setPage(currentPage - 1)} aria-label="Previous suggestions">←</button><button type="button" disabled={currentPage >= pageCount - 1 || Boolean(editingId)} onClick={() => setPage(currentPage + 1)} aria-label="Next suggestions">→</button></div>
            </div>
            <div className="td-candidates" aria-label={`${meta.label} suggestions`}>
              {visibleItems.map(item => <article className={`td-candidate${selected.includes(item.id) ? ' is-selected' : ''}`} key={item.id}>
                <div className="td-candidate-heading"><span>{item.heading}</span>{selected.includes(item.id) && <span className="td-added">✓ In your draft</span>}</div>
                <p className="td-candidate-text">{item.text}</p>
                {editingId === item.id ? renderEditor(item) : <div className="td-candidate-actions">
                  <button type="button" className={selected.includes(item.id) ? 'td-selected-button' : 'td-keep'} disabled={Boolean(editingId)} aria-pressed={selected.includes(item.id)} aria-label={`${selected.includes(item.id) ? 'Remove from' : 'Add to'} draft: ${item.text}`} onClick={() => toggleSelection(item)}>{selected.includes(item.id) ? 'Remove from draft' : '+ Add to draft'}</button>
                  <button ref={node => { editButtonsRef.current[item.id] = node; }} type="button" className="td-edit" disabled={Boolean(editingId)} aria-label={`Edit suggestion: ${item.text}`} onClick={() => startEdit(item)}>Edit wording</button>
                </div>}
                <details className="td-evidence"><summary>Why this suggestion?</summary><span>{item.sourceLabel}</span><blockquote>{item.source}</blockquote><p>{item.type === 'titles' ? 'A career direction to research; this does not establish qualifications or past employment.' : 'Suggested wording from your self-reported details. Check that it accurately describes your work.'}</p></details>
              </article>)}
              {!filtered.length && <div className="td-empty"><p>{query ? 'Try a different word or clear your search.' : 'No supported suggestions in this category yet. You can continue with another category.'}</p>{query && <button type="button" onClick={() => { setQuery(''); setPage(0); }}>Clear search</button>}</div>}
            </div>
            {pageCount > 1 && <div className="td-pagination-footer" role="group" aria-label="Continue browsing suggestions">
              <button type="button" disabled={currentPage === 0 || Boolean(editingId)} onClick={() => { setPage(currentPage - 1); setFocusLibrary(true); }}>← Earlier suggestions</button>
              <span>Page {currentPage + 1} of {pageCount}</span>
              <button type="button" disabled={currentPage >= pageCount - 1 || Boolean(editingId)} onClick={() => { setPage(currentPage + 1); setFocusLibrary(true); }}>More suggestions →</button>
            </div>}
          </div>
          <aside className="td-draft-sidebar" aria-label="Your draft summary">
            <div className="td-draft-head"><span className="td-eyebrow">TAKING SHAPE</span><span className="td-draft-count">{chosen.length} selected</span></div>
            <h4>Your civilian story</h4>
            {!chosen.length ? <div className="td-sidebar-empty"><span aria-hidden="true">＋</span><p>Start with a statement that sounds like work you did.</p><small>Your choices will appear here.</small></div> : <>
              <p className="td-sidebar-caption">A preview of what you’re keeping.</p>
              <ul className="td-shortlist">{chosen.slice(0, 3).map(item => <li key={item.id}>{item.text}</li>)}</ul>
              {chosen.length > 3 && <p className="td-more">+ {chosen.length - 3} more in your draft</p>}
            </>}
            <dl className="td-counts">{CATEGORIES.map(({ key, label }) => <div key={key}><dt>{label}</dt><dd>{chosen.filter(item => item.type === key).length}</dd></div>)}</dl>
            <button type="button" className="td-primary" disabled={!chosen.length || Boolean(editingId)} onClick={() => goTo('review')}>Review my draft <span aria-hidden="true">→</span></button>
            <small>Choose only what is useful. Every category is optional.</small>
          </aside>
        </div>
        <div className="td-mobile-review"><span>{chosen.length} selected</span><button type="button" className="td-primary" disabled={!chosen.length || Boolean(editingId)} onClick={() => goTo('review')}>Review my draft →</button></div>
      </> : stage === 'review' ? <>
        <div className="td-review-toolbar"><button type="button" disabled={Boolean(editingId)} onClick={() => goTo('choose')}>← Back to suggestions</button><span>{chosen.length} selected · Unselected items are excluded</span></div>
        {renderDraft(true)}
        <div className="td-approval-bar">
          <label><input type="checkbox" checked={attested} disabled={Boolean(editingId) || !chosen.length} onChange={event => setAttested(event.target.checked)} /><span>I reviewed my selected wording for accuracy. Career ideas are roles to explore.</span></label>
          <button type="button" className="td-primary" disabled={!chosen.length || !attested || Boolean(editingId)} onClick={approve}>Approve my selected draft</button>
        </div>
      </> : busy ? <div className="td-confirmation" role="status"><span aria-hidden="true">✓</span><h4>Reviewed. Refined. Yours.</h4><p>Approving your selected translation for this visit.</p></div> : <>
        <div className="td-approved"><span className="td-seal" aria-hidden="true">✓</span><div><span className="td-eyebrow">REVIEWED & PRIVATE</span><h4>Your experience has a civilian voice.</h4><p>{chosen.length} selected {chosen.length === 1 ? 'item' : 'items'} approved for this visit. Copy your wording before leaving the page.</p></div></div>
        <div className="td-approved-actions"><button type="button" className="td-primary" onClick={copyApproved}>Copy approved translation</button><button type="button" onClick={() => setShowApproved(current => !current)} aria-expanded={showApproved}>{showApproved ? 'Hide approved draft' : 'View approved draft'}</button><button type="button" onClick={() => { setAttested(false); changed('Review reopened. Approve again after making changes.'); goTo('review'); }}>Edit my draft</button></div>
        {showApproved && renderDraft(false)}
        {copyFallback && <label className="td-copy-fallback">Approved translation<textarea readOnly value={approvedText()} rows={8} onFocus={event => event.target.select()} /></label>}
      </>}
    <p className="td-session-note">{SESSION_COPY}</p>
  </section>;
}
